/** @jsx isolateComponent */

import type { KeyboardEvent as ReactKeyboardEvent } from 'react';

import { emitUserInteractionEvent } from '../../utils/avroEvents';
import { isolateComponent, labelStyles } from '@zapier/style-encapsulation';
import { useClickOutside, ServiceTypeahead } from '@zapier/design-system';
import { useUniversalNavContext } from '../../hooks';

import SearchToggler from './OutOfAppTopbar/SearchToggler';
export type Props = {
  /*
   * Indicates whether the user is currently searching.
   */
  isSearching: boolean;
  /*
   * The `id` attribute of the search `input`, used for hooking
   * `input` and `label` together.
   */
  searchInputId: string;
  /*
   * Dictates what happens when searching is closed.
   */
  onClose: () => void;
};

const Styles = labelStyles('SearchForm', {
  root: ({ isSearching }: Props) => [
    {
      display: 'grid',
      gridTemplateColumns: '1fr auto',
      gap: 10,
      width: isSearching ? '100%' : 'auto',
      maxWidth: 500,
    },
  ],
});
/**
 * @private
 * Renders a `ServiceTypehead` to allow users to search for apps.
 */
const SearchForm = (props: Props) => {
  const { apiHostname } = useUniversalNavContext();

  const [setClickOutsideNode] = useClickOutside({
    onClickOutside: () => props.onClose(),
  });
  const onEscape = (e: ReactKeyboardEvent) => {
    if (e.key === 'Escape') {
      props.onClose();
    }
  };

  const handleChange = (app: any) => {
    const { location } = window;

    const isDevOrStaging =
      location.origin.includes('localhost') ||
      location.origin.includes('zapier-staging');

    const isZapier = location.origin.startsWith('https://zapier.com');

    if (app && app.slug) {
      const url = `/apps/${app.slug}/integrations`;
      emitUserInteractionEvent({
        interaction_name: 'universalNavServiceTypeahead',
        event_action: 'click',
        option_selected: app.slug,
      });

      if (isDevOrStaging || isZapier)
        location.href = `${location.origin}${url}`;
      else location.href = `https://zapier.com${url}`;
    }
  };

  return (
    <div css={Styles.root(props)} role="form">
      {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions */}
      <div onKeyUp={onEscape} ref={setClickOutsideNode}>
        <ServiceTypeahead
          apiHostname={apiHostname}
          ariaLabel="Search Apps"
          excludeUpcoming={false}
          id="universalNavServiceTypeahead"
          inputId={props.searchInputId}
          menuAriaLabel="List of available apps"
          onChange={(app) => handleChange(app)}
          placeholder="Search by App"
          resultsLimit={8}
        />
      </div>
      <SearchToggler
        ariaHidden={true}
        ariaLabel="Close search"
        isSearching={true}
      />
    </div>
  );
};

export default SearchForm;
